import { useApolloClient } from "@apollo/client";
import { EditorState } from "draft-js";
import {
  ErrorMessage,
  FieldArray,
  ArrayHelpers,
  Field,
  FieldInputProps,
  FormikProps,
  useFormikContext
} from "formik";
import _ from "lodash";
import { ChangeEvent, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { MenuItem, Button, Tooltip, makeStyles } from "@material-ui/core";
import { CloudUpload } from "@material-ui/icons";

import { Tenant, useTenant } from "src/components/LoggedInAdminProvider";
import { Subaccount, useSubaccount } from "src/components/SubaccountProvider";
import FormVanSearchField, {
  VanSearchFieldValue
} from "src/components/form/FormVanSearchField";
import FormikTextField from "src/components/form/FormikTextField";
import { FormikSelect } from "src/components/form/Select";
import { CampaignWithCalleeFields_calleeFields } from "src/graphql/generated/CampaignWithCalleeFields";
import { getCampaign_campaign } from "src/graphql/generated/getCampaign";
import {
  getCampaignVanSurveyQuestionData,
  getCampaignVanSurveyQuestionDataVariables
} from "src/graphql/generated/getCampaignVanSurveyQuestionData";
import {
  QuestionType,
  VanProxySearchType
} from "src/graphql/generated/globalTypes";
import { messagingNumberIsActive } from "src/util/messaging";

import Answer from "./Answer";
import MobilizeAmericaRSVPSettings from "./MobilizeAmericaRSVPSettings";
import NextQuestion from "./NextQuestion";
import PollingPlaceLookupSettings from "./PollingPlaceLookupSettings";
import SendMessageSettings from "./SendMessageSettings";
import TextEditor from "./TextEditor";
import VANCanvassResultImport from "./VANCanvassResultImport";
import VANQuestionImport from "./VANQuestionImport";
import { GET_QUESTION_DATA } from "./queries";
import { FormQuestion } from "./types";

const MAX_ANSWERS = 250;

function questionTypeOptions(
  tenant: Tenant,
  campaign: getCampaign_campaign,
  subaccount: Subaccount
): {
  value: QuestionType;
  label: string;
  disabled?: boolean;
}[] {
  const options: {
    value: QuestionType;
    label: string;
    disabled?: boolean;
    supportsIVR?: boolean;
    disabledForBandwidth?: boolean;
  }[] = [
    {
      value: QuestionType.SINGLE_CHOICE,
      label: "Single Choice",
      supportsIVR: true
    },
    { value: QuestionType.MULTIPLE_CHOICE, label: "Checkboxes" },
    { value: QuestionType.TEXT_FIELD, label: "Short Answer" },
    {
      value: QuestionType.SEND_MESSAGE,
      label: "Text Message",
      disabled:
        !subaccount.messagingPhoneNumber ||
        !messagingNumberIsActive(
          tenant.featureFlags.preventMessagingFromUnregisteredNumbers,
          subaccount.messagingPhoneNumberStatus
        )
    }
  ];

  if (tenant.featureFlags.patchThru) {
    options.push({
      value: QuestionType.PATCH_THRU,
      label: "Patch Through Call",
      disabledForBandwidth: true
    });
  }

  if (tenant.featureFlags.mobilizeAmericaRsvp) {
    options.push({
      value: QuestionType.MOBILIZEAMERICA_RSVP,
      label: "Mobilize America RSVP"
    });
  }

  options.push({
    value: QuestionType.POLLING_PLACE_LOOKUP,
    label: "Polling Place Lookup"
  });

  for (const option of options) {
    if (campaign.ivr && !option.supportsIVR) {
      option.disabled = true;
      option.label += " - not enabled in phone-only mode";
    } else if (
      campaign.provider === "bandwidth" &&
      option.disabledForBandwidth
    ) {
      option.disabled = true;
      option.label += " - not enabled in backup provider";
    } else if (option.disabled) {
      option.label += " - not enabled";
    }
  }

  return options;
}

export const QUESTION_TYPES_WITH_ANSWERS = [
  QuestionType.DISPOSITION,
  QuestionType.SINGLE_CHOICE,
  QuestionType.MULTIPLE_CHOICE,
  QuestionType.PATCH_THRU,
  QuestionType.POLLING_PLACE_LOOKUP
];

export const DISPOSITION_NEXT_ACTIONS = [
  "CANVASSED",
  "ANSWERING_MACHINE",
  "NO_CALLBACK",
  "CALLBACK",
  "OPT_OUT"
];

export const DISPOSITION_ACTION_TITLES: Record<string, string> = {
  CANVASSED: "Canvassed",
  ANSWERING_MACHINE: "Answering machine",
  NO_CALLBACK: "Do not retry to call",
  CALLBACK: "Retry to call on a subsequent pass",
  OPT_OUT: "Add to 'Do Not Call' list"
};

const useStyles = makeStyles(theme => ({
  answerGroup: {
    marginLeft: theme.spacing(6)
  },
  addAnswerWrapper: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    marginLeft: theme.spacing(6),
    marginBottom: theme.spacing(6)
  },
  errorMessage: {
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2)
  },
  scriptInput: {
    "& textarea": {
      minHeight: 40
    }
  },
  canvassResultImportButton: {
    display: "inline-block",
    marginLeft: theme.spacing(2)
  },
  importQuestionButton: {
    marginBottom: theme.spacing(2)
  }
}));

function VANSurveyQuestionPicker({
  question,
  name,
  campaign
}: {
  question: FormQuestion;
  name: string;
  campaign: getCampaign_campaign;
}): JSX.Element | null {
  const { setFieldValue } = useFormikContext<unknown>();
  const apolloClient = useApolloClient();

  if (!campaign.vanIntegration) {
    return null;
  }

  let vanSurveyQuestionValue: VanSearchFieldValue | null = null;
  const vanSurveyQuestionId = question.vanIntegrationSettings?.surveyQuestionId;
  if (vanSurveyQuestionId) {
    vanSurveyQuestionValue = {
      id: vanSurveyQuestionId,
      label: question.vanIntegrationSettings!.surveyQuestionData?.name ?? ""
    };
  }

  async function handleSurveyQuestionChange(
    evt: ChangeEvent<any>,
    value: VanSearchFieldValue | null
  ) {
    setFieldValue(`${name}.vanIntegrationSettings.surveyQuestionId`, value?.id);
    setFieldValue(
      `${name}.vanIntegrationSettings.surveyQuestionData`,
      value ? { name: value.label } : null
    );

    // load answers
    if (value) {
      const { data, error } = await apolloClient.query<
        getCampaignVanSurveyQuestionData,
        getCampaignVanSurveyQuestionDataVariables
      >({
        query: GET_QUESTION_DATA,
        variables: {
          campaignId: campaign.id,
          vanSurveyQuestionId: value.id
        }
      });

      if (error) {
        console.error(error);
      } else if (data) {
        setFieldValue(
          `${name}.vanIntegrationSettings.surveyQuestionData`,
          data.campaign.vanIntegration?.surveyQuestion
        );
      }
    }
  }

  return (
    <FormVanSearchField
      fullWidth
      label="NGP VAN Survey Question"
      value={vanSurveyQuestionValue}
      onChange={handleSurveyQuestionChange}
      campaignId={campaign.id}
      searchType={VanProxySearchType.SURVEY_QUESTION}
    />
  );
}

export default function Question({
  question,
  isFirstQuestion,
  name,
  onGoToQuestion,
  onCreateFollowupQuestion,
  campaign,
  onCreateCalleeField
}: {
  question: FormQuestion;
  isFirstQuestion: boolean;
  name: string;
  onGoToQuestion: (questionKey: string) => void;
  onCreateFollowupQuestion: (questionKey: string, answerIndex?: number) => void;
  campaign: getCampaign_campaign;
  onCreateCalleeField: (
    name: string
  ) => Promise<CampaignWithCalleeFields_calleeFields>;
}): JSX.Element {
  const tenant = useTenant()!;
  const { subaccount } = useSubaccount()!;
  const classes = useStyles();
  const [canvasResultImportDialogOpen, setCanvasResultImportDialogOpen] =
    useState(false);
  const [vanQuestionImportDialogOpen, setVanQuestionImportDialogOpen] =
    useState(false);

  const canvasResultImportEnabled =
    !!campaign.vanIntegration &&
    isFirstQuestion &&
    [QuestionType.DISPOSITION, QuestionType.SINGLE_CHOICE].includes(
      question.questionType
    ) &&
    question.answers.every(
      answer => !answer.vanIntegrationSettings?.resultCodeId
    );

  function maybeRenderVANSurveyQuestionImport() {
    if (!campaign.vanIntegration) {
      // can't import if this campaign isn't linked to VAN
      return null;
    }

    if (isFirstQuestion) {
      // The first question can only sync canvass results to VAN, because VAN
      // only lets you sync survey questions after syncing a "canvassed" result.
      return null;
    }

    // Otherwise, we're good to import a survey question from VAN
    return (
      <>
        <Button
          variant="contained"
          onClick={() => setVanQuestionImportDialogOpen(true)}
          startIcon={<CloudUpload />}
          className={classes.importQuestionButton}
        >
          Import Question From NGP VAN
        </Button>
        <VANQuestionImport
          name={name}
          open={vanQuestionImportDialogOpen}
          onClose={() => setVanQuestionImportDialogOpen(false)}
          question={question}
          campaign={campaign}
          maxAnswers={MAX_ANSWERS}
        />
      </>
    );
  }

  function maybeRenderVANSurveyQuestionPicker() {
    if (isFirstQuestion) {
      // as above, we don't support survey questions on the first question;
      // you have to do canvass results first
      return null;
    }

    if (
      ![QuestionType.DISPOSITION, QuestionType.SINGLE_CHOICE].includes(
        question.questionType
      )
    ) {
      // survey questions can only be used with the "single choice" or
      // "disposition" question types
      return null;
    }

    return (
      <VANSurveyQuestionPicker
        question={question}
        name={name}
        campaign={campaign}
      />
    );
  }

  function maybeRenderNextActionPicker() {
    if (
      [
        QuestionType.DISPOSITION,
        QuestionType.SINGLE_CHOICE,
        QuestionType.PATCH_THRU
      ].includes(question.questionType)
    ) {
      // single-choice questions let you pick a different "next action" for each
      // response, so we don't need a picker here -- it's rendered by each answer
      return null;
    }

    return (
      <div className={classes.answerGroup}>
        <NextQuestion
          name={`${name}.next`}
          value={question.next || ""}
          questionKey={question.key}
          enablePatchThru={false}
          onGoToQuestion={onGoToQuestion}
          onCreateFollowupQuestion={() =>
            onCreateFollowupQuestion(question.key)
          }
        />
      </div>
    );
  }

  function renderOptionsForQuestionType() {
    if (question.questionType === QuestionType.POLLING_PLACE_LOOKUP) {
      return (
        <div className={classes.answerGroup}>
          <PollingPlaceLookupSettings
            campaign={campaign}
            lookupToolFieldName={`${name}.pollingPlaceLookupTool`}
          />
        </div>
      );
    }

    if (QUESTION_TYPES_WITH_ANSWERS.includes(question.questionType)) {
      return (
        <FieldArray name={`${name}.answers`}>
          {({ push, remove }: ArrayHelpers) => {
            const onAddAnswer = () => {
              push({
                __typename: "CampaignAnswer",
                key: uuidv4(), // placeholder, filled in when we send to the server
                value: "",
                next: null,
                vanIntegrationSettings: {
                  activistCode: null,
                  surveyResponseId: null,
                  resultCodeId: null
                }
              });
            };

            return (
              <>
                <div className={classes.answerGroup}>
                  {question.answers.map((answer, answerIndex) => (
                    <Answer
                      key={answer.key}
                      answer={answer}
                      answerIndex={answerIndex}
                      name={`${name}.answers[${answerIndex}]`}
                      questionKey={question.key}
                      onRemoveAnswer={remove}
                      onGoToQuestion={onGoToQuestion}
                      onCreateFollowupQuestion={() =>
                        onCreateFollowupQuestion(question.key, answerIndex)
                      }
                      campaign={campaign}
                      enableNextQuestion={[
                        QuestionType.DISPOSITION,
                        QuestionType.SINGLE_CHOICE,
                        QuestionType.PATCH_THRU
                      ].includes(question.questionType)}
                      enableDispositionAnswers={
                        question.questionType === QuestionType.DISPOSITION
                      }
                      enableVANSurveyResponse={
                        question.questionType === QuestionType.SINGLE_CHOICE &&
                        !isFirstQuestion
                      }
                      enableVANActivistCode={
                        question.questionType !== QuestionType.DISPOSITION
                      }
                      enableVANCanvassResult={
                        [
                          QuestionType.DISPOSITION,
                          QuestionType.SINGLE_CHOICE
                        ].includes(question.questionType) && isFirstQuestion
                      }
                      enablePatchThru={
                        question.questionType === QuestionType.PATCH_THRU
                      }
                    />
                  ))}
                </div>

                <div className={classes.addAnswerWrapper}>
                  {question.answers.length >= MAX_ANSWERS ? (
                    <Tooltip
                      title={`You can only add ${MAX_ANSWERS} answers for each question`}
                    >
                      <span>
                        <Button variant="contained" disabled>
                          + Add Answer
                        </Button>
                      </span>
                    </Tooltip>
                  ) : (
                    <>
                      <Button variant="contained" onClick={onAddAnswer}>
                        + Add Answer
                      </Button>
                      {canvasResultImportEnabled && (
                        <Button
                          variant="contained"
                          className={classes.canvassResultImportButton}
                          onClick={() => setCanvasResultImportDialogOpen(true)}
                        >
                          + Import NGP VAN Canvass Results
                        </Button>
                      )}
                    </>
                  )}
                </div>
                {canvasResultImportEnabled && (
                  <VANCanvassResultImport
                    open={canvasResultImportDialogOpen}
                    onClose={() => setCanvasResultImportDialogOpen(false)}
                    addAnswer={push}
                    campaign={campaign}
                    maxAnswers={MAX_ANSWERS}
                  />
                )}
              </>
            );
          }}
        </FieldArray>
      );
    }

    if (question.questionType === QuestionType.MOBILIZEAMERICA_RSVP) {
      return (
        <div className={classes.answerGroup}>
          <MobilizeAmericaRSVPSettings
            eventUrlFieldName={`${name}.mobilizeAmericaEventUrl`}
            campaign={campaign}
          />
        </div>
      );
    }

    if (question.questionType === QuestionType.SEND_MESSAGE) {
      return (
        <div className={classes.answerGroup}>
          <SendMessageSettings
            campaign={campaign}
            textFieldName={`${name}.sendMessageText`}
            onCreateCalleeField={onCreateCalleeField}
          />
        </div>
      );
    }

    return null;
  }

  return (
    <>
      <ErrorMessage name={`${name}.answers`}>
        {msg => {
          return (
            <div className={classes.errorMessage}>
              {/* We need to check typeof msg here -- if there's a nested error,
                that'll show up as an object but we just want to show top-level
                errors */}
              {typeof msg === "string" ? msg : null}
            </div>
          );
        }}
      </ErrorMessage>

      {maybeRenderVANSurveyQuestionImport()}

      {question.questionType !== QuestionType.DISPOSITION && (
        <FormikTextField
          fast
          fullWidth
          label="Question Title"
          placeholder="Write the question you want your callers to read"
          name={`${name}.name`}
        />
      )}

      <Field name={`${name}.script`}>
        {({
          field,
          form
        }: {
          field: FieldInputProps<EditorState>;
          form: FormikProps<any>;
        }) => (
          <TextEditor
            label={`${
              question.questionType === QuestionType.DISPOSITION
                ? "Intro"
                : "Question"
            } Script`}
            placeholder="Write the script you want your callers to read"
            editorState={field.value}
            onChangeEditorState={newState =>
              form.setFieldValue(field.name, newState)
            }
            calleeFields={campaign.calleeFields}
            canUpdateCalleeFields={campaign.status === "DRAFT"}
            onCreateCalleeField={onCreateCalleeField}
          />
        )}
      </Field>

      {question.questionType !== QuestionType.DISPOSITION && (
        <FormikSelect
          name={`${name}.questionType`}
          label="Question Type"
          placeholder="Select a question type"
          fullWidth
        >
          {questionTypeOptions(tenant, campaign, subaccount).map(
            ({ value, label, disabled }) => (
              <MenuItem key={value} value={value} disabled={disabled}>
                {label}
              </MenuItem>
            )
          )}
        </FormikSelect>
      )}

      {maybeRenderVANSurveyQuestionPicker()}

      {renderOptionsForQuestionType()}

      {maybeRenderNextActionPicker()}
    </>
  );
}
